import React from "react";
import dayjs from "../../../../../utils/dayjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faTrashAlt,
  faUserCircle,
  faClock,
  faCheck,
  faCalendarAlt,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";
import {
  showConfirmDialog,
  showSuccessToast,
  showErrorToast,
} from "../../../../../components/ConfirmDialog";

const TaskCardWaiting = ({
  task,
  currentUserId,
  onSelect,
  onEdit,
  onDelete,
}) => {
  const taskItems = Array.isArray(task.task_list)
    ? task.task_list.slice(0, 3)
    : [];
  const taskCount = Array.isArray(task.task_list)
    ? task.task_list.length
    : 0;

  // console.log("CARD TASK >>>", task);

  // 🗓 Ngày thực hiện
  const today = dayjs().tz().startOf("day");
  const workDate = task.work_day
    ? dayjs(task.work_day).tz().startOf("day")
    : dayjs.invalid();

  // 🧮 Tính số ngày còn lại
  const diffDays = workDate.diff(today, "day");
  const formattedDate = workDate.isValid()
    ? workDate.format("DD-MMM-YYYY")
    : "-";
  const label = `Đăng ký ${taskCount} việc ngày ${formattedDate}`;

  let tagText = "";
  let tagColor = "";

  if (diffDays > 0) {
    tagText = `Còn ${diffDays} ngày`;
    tagColor = "bg-green-100 text-green-700 border border-green-300";
  } else if (diffDays === 0) {
    tagText = "Đến hạn hôm nay";
    tagColor = "bg-yellow-100 text-yellow-700 border border-yellow-300";
  } else {
    tagText = `Quá hạn ${Math.abs(diffDays)} ngày`;
    tagColor = "bg-red-100 text-red-700 border border-red-300";
  }

  // ✅ Avatar
  const avatar = task.avatar_url ? (
    <img
      src={task.avatar_url}
      alt="avatar"
      className="w-8 h-8 rounded-full mr-2 object-cover"
    />
  ) : (
    <div className="w-8 h-8 rounded-full mr-2 bg-gray-200 flex items-center justify-center">
      <FontAwesomeIcon
        icon={faUserCircle}
        className="text-gray-400 text-xl"
      />
    </div>
  );

  // ✅ Trạng thái sang tiếng Việt
  const getStatusInfo = (status) => {
    switch ((status || "").toLowerCase()) {
      case "waiting":
      case "pending":
        return { text: "Chờ duyệt", color: "bg-yellow-100 text-yellow-700" };
      case "approved":
        return { text: "Đã duyệt", color: "bg-green-100 text-green-700" };
      case "reported":
        return { text: "Đã báo cáo", color: "bg-blue-100 text-blue-700" };
      case "completed":
        return { text: "Hoàn thành", color: "bg-gray-200 text-gray-700" };
      case "rejected":
        return { text: "Không duyệt", color: "bg-red-100 text-red-700" };
      default:
        return { text: "Không xác định", color: "bg-gray-100 text-gray-700" };
    }
  };

  const { text: statusText, color: statusColor } = getStatusInfo(task.status);

  // ✅ Xác định quyền hiển thị nút
  const isOwner = currentUserId && task.employee_id === currentUserId;

  // 🎨 Màu nền toàn thẻ dựa theo hạn
  let cardBg = "bg-white";
  if (diffDays < 0) cardBg = "bg-red-50"; // quá hạn
  else if (diffDays === 0) cardBg = "bg-yellow-50"; // đến hạn
  else cardBg = "bg-white"; // bình thường

  return (
    <div
      onClick={() =>
        onSelect?.({
          employee_id: task.employee_id,
          employee_name: task.employee_name,
          work_day: dayjs(task.work_day).format("YYYY-MM-DD"),
        })
      }
      className={`group relative w-full border rounded-2xl p-4 shadow-sm 
        hover:shadow-xl hover:border-red-400 hover:-translate-y-1
        active:scale-[0.98] transition-all duration-300 cursor-pointer 
        flex flex-col min-h-[190px] ${cardBg} overflow-hidden`}
    >
      {/* --- HEADER LAYER: Status & Time --- */}
      <div className="flex justify-between items-start mb-4">
        {/* 🕒 Tag đếm ngược - Thiết kế gọn & hiện đại hơn */}
        <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg font-bold text-[10px] uppercase tracking-wider ${tagColor} bg-opacity-10 backdrop-blur-sm border border-current/20`}>
          <FontAwesomeIcon icon={faClock} />
          {tagText}
        </div>

        {/* 🏷️ Badge Trạng thái - Modern Ribbon Design */}
        {task.status === "waiting" && (
          <div className="absolute top-0 right-0 z-10 flex flex-col items-end">
            <div
              className={`
        relative px-3 py-1.5 text-[10px] font-black tracking-widest uppercase
        rounded-bl-xl shadow-sm flex items-center gap-1.5 transition-all duration-300
        ${task.can_approve
                  ? "bg-gradient-to-br from-red-500 to-rose-600 text-white animate-pulse"
                  : "bg-gradient-to-br from-amber-400 to-orange-400 text-white"
                }
      `}
            >
              {/* Hiệu ứng bóng đổ nội khối để chữ nổi bật hơn */}
              <div className="absolute inset-0 bg-black/5 rounded-bl-xl pointer-events-none"></div>

              <FontAwesomeIcon
                icon={task.can_approve ? faCheckCircle : faClock}
                className={`${task.can_approve ? "text-white" : "text-amber-100"} text-[11px]`}
              />

              <span className="relative">
                {task.can_approve ? "Cần bạn duyệt" : "Chờ duyệt"}
              </span>

              {/* Trang trí thêm một chấm sáng nhỏ nếu cần duyệt */}
              {task.can_approve && (
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
                </span>
              )}
            </div>

            {/* Phần "tai" nhỏ tạo hiệu ứng 3D nếu bạn muốn ribbon trông như quấn quanh card */}
            <div className={`w-0 h-0 border-l-[6px] border-l-transparent border-t-[6px] 
      ${task.can_approve ? "border-t-red-700" : "border-t-amber-600"}`}
            ></div>
          </div>
        )}
      </div>

      {/* --- INFO LAYER: Avatar & Name --- */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative">
          <div className="ring-2 ring-white shadow-md rounded-full">
            {avatar}
          </div>
          {/* Chấm xanh trạng thái nếu cần online/offline */}
          <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></div>
        </div>

        <div className="flex-1 min-w-0">
          <h4 className="font-bold text-gray-900 text-sm leading-tight truncate group-hover:text-red-600 transition-colors">
            {task.employee_name}
          </h4>
          <p className="text-[11px] text-gray-500 font-medium truncate">
            {task.department_name || task.branch_name || "—"}
          </p>
        </div>
      </div>

      {/* --- CONTENT LAYER: Task List --- */}
      <div className="flex-1">
        {task.tags && task.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {task.tags.slice(0, 2).map((tag) => ( // Giới hạn hiển thị 2 tag để tránh vỡ layout
              <span key={tag.tag_id} className="text-[9px] font-bold px-2 py-0.5 rounded bg-gray-100 text-gray-600 border border-gray-200 uppercase">
                #{tag.tag_name}
              </span>
            ))}
          </div>
        )}

        <div className="space-y-1.5 pl-1">
          {taskItems.length > 0 ? (
            taskItems.slice(0, 3).map((work, i) => ( // Chỉ hiện tối đa 3 task đầu, còn lại xem trong popup
              <div key={i} className="flex items-center gap-2 text-[13px] text-gray-600">
                <div className="w-1 h-1 rounded-full bg-gray-400 shadow-[0_0_5px_rgba(96,165,250,0.8)]"></div>
                <span className="flex-1 truncate leading-tight italic font-medium">
                  {work.taskname}
                </span>
              </div>
            ))
          ) : (
            <div className="text-[12px] italic text-gray-400 py-1">Chưa có công việc được ghi nhận</div>
          )}
          {taskItems.length > 3 && (
            <div className="text-[10px] text-gray-500 font-bold ml-3">+ {taskItems.length - 3} công việc khác...</div>
          )}
        </div>
      </div>

      {/* --- FOOTER LAYER: Date & Actions --- */}
      <div className="mt-4 pt-3 border-t border-gray-100 flex justify-between items-center">
        <span className="text-[10px] font-medium text-gray-400 flex items-center gap-1 uppercase tracking-tighter">
          <FontAwesomeIcon icon={faCalendarAlt} className="text-[9px]" />
          {label}
        </span>

        {isOwner && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete?.(task);
            }}
            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
            title="Xóa nhanh"
          >
            <FontAwesomeIcon icon={faTrashAlt} />
          </button>
        )}
      </div>
    </div>
  );


};

export default TaskCardWaiting;
